import normalize from "json-api-normalizer";
import { camelizeKeys } from "humps";
import _ from "lodash";
import axios from "axios";
import ls from "local-storage";
import store from "@/store/index";

import BaseService from "./BaseService";

export default {
  list(search = "", page = 1) {
    return BaseService.get(
      `admin/practices?search=${search}&page[number]=${page}`
    ).then(response => {
      let practices = {};
      if (response.data.data.length) {
        practices = this.fixCamel(normalize(response.data).practices);
      }

      store.dispatch("practice/setList", {
        practices,
        links: response.data.links
      });
      const keys = Object.keys(practices);
      return {
        first: keys[0],
        count: keys.length,
        subdomain: keys.length
          ? practices[keys[0]].attributes.subdomain
          : "public"
      };
    });
  },

  get(id) {
    return new Promise((resolve, reject) => {
      BaseService.get(`admin/practices/${id}`)
        .then(response => {
          let practice = response.data.data;
          practice.attributes = camelizeKeys(practice.attributes);
          resolve(practice);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  create(values) {
    return BaseService.post("practices", "admin/practices", values)
      .then(response => {
        const practice = this.fixCamel(normalize(response.data).practices);
        store.dispatch("practice/add", practice);
      })
      .catch(errors => Promise.reject(errors));
  },

  update(values) {
    return BaseService.patch(
      "practices",
      `admin/practices/${values.id}`,
      values
    )
      .then(response => {
        const practice = this.fixCamel(normalize(response.data).practices);
        store.dispatch("practice/update", practice);
      })
      .catch(errors => Promise.reject(errors));
  },

  fixCamel(list) {
    const practice = list;
    _.forEach(Object.keys(practice), key => {
      practice[key].attributes = camelizeKeys(practice[key].attributes);
    });

    return practice;
  },

  delete(id) {
    return BaseService.delete(`admin/practices/${id}`)
      .then(() => Promise.resolve({ id }))
      .catch(error => Promise.reject(error.response.data));
  },

  uploadLogo(id, logo) {
    const user = ls.get("user");
    axios.defaults.headers.common.Authorization = `Bearer ${
      user.jwt.access_token
    }`;
    const config = { headers: { "Content-Type": "multipart/form-data" } };

    const data = new FormData();
    data.append("logo", logo);

    return axios
      .patch(`${BaseService.apiUrl()}/v1/practices/${id}/logo`, data, config)
      .then(response => {
        const practice = normalize(response.data).practices;
        store.dispatch("practice/update", practice);
      });
  },

  getUsers(practiceId) {
    return BaseService.get(`practices/${practiceId}/users`).then(response => {
      const users = normalize(response.data).users;
      store.dispatch("practice/setUsers", users);
    });
  },

  updateUser(values) {
    return BaseService.patch("users", `users/${values.id}`, values)
      .then(response => {
        const user = normalize(response.data).users;
        store.dispatch("practice/updateUser", user);
      })
      .catch(errors => Promise.reject(errors));
  },

  updateProfile(values) {
    return BaseService.patch("users", `users/${values.id}`, values)
      .then(response => {
        const user = Object.values(normalize(response.data).users)[0];
        store.dispatch("setUser", user);
      })
      .catch(errors => Promise.reject(errors));
  },

  createUser(values) {
    return BaseService.post("users", "users", values)
      .then(response => {
        const user = normalize(response.data).users;
        store.dispatch("practice/addUser", user);
      })
      .catch(errors => Promise.reject(errors));
  },

  deleteUser(id) {
    return BaseService.delete(`users/${id}`)
      .then(() => Promise.resolve({ id }))
      .catch(error => Promise.reject(error.response.data));
  },

  reinstateUser(id) {
    return BaseService.patch("users", `users/${id}/reinstate`, { id })
      .then(() => Promise.resolve({ id }))
      .catch(error => Promise.reject(error.response.data));
  },

  initializeInterview(values) {
    const url = `practices/${values.practiceId}/initialize-interview/${
      values.interviewId
    }`;
    return BaseService.patch("practices", url, values)
      .then(() => this.list())
      .catch(errors => Promise.reject(errors));
  },

  cloneInterview(values) {
    const url = `practices/${values.practiceId}/clone-interview/${
      values.interviewId
    }`;
    return BaseService.patch("practices", url, values).catch(errors =>
      Promise.reject(errors)
    );
  },

  updateCss() {
    return BaseService.get("practices/update-css");
  },

  billingCsv() {
    return BaseService.get("practices/billing-csv").then(
      response => response.data
    );
  },

  getTimezones() {
    return BaseService.get("practices/timezones").then(response => {
      store.dispatch("practice/setTimezones", response.data);
    });
  },

  updateKeyword(practiceId, keyword, oldKeyword) {
    const values = {
      id: practiceId,
      keyword,
      oldKeyword
    };
    return BaseService.patch(
      "practices",
      `practices/${practiceId}/interview-keyword`,
      values
    )
      .then(() => {
        this.list();
        return Promise.resolve();
      })
      .catch(error => {
        let errorString = "";
        if (typeof error.response.data.errors[0].detail === "object") {
          errorString = error.response.data.errors[0].detail[0].detail;
        } else {
          errorString = error.response.data.errors[0].detail;
        }
        return Promise.reject(errorString);
      });
  },

  deleteKeyword(practiceId, keyword) {
    return BaseService.delete(
      `practices/${practiceId}/interview-keyword/${keyword}`
    )
      .then(() => {
        this.list();
        return Promise.resolve();
      })
      .catch(errors => Promise.reject(errors));
  },

  userSearch(query) {
    store.dispatch("practice/setUserSearchMessage", "");

    return BaseService.post("users", "users/search", { query })
      .then(response => {
        const data = normalize(response.data);
        if (data.users) {
          store.dispatch("practice/userSearch", data.users);
        } else {
          store.dispatch("practice/userSearch", []);
          store.dispatch(
            "practice/setUserSearchMessage",
            "Cannot find a user with that search term."
          );
        }
      })
      .catch(errors => Promise.reject(errors));
  },

  addUser(practiceId, userId) {
    return BaseService.post("practices", `practices/${practiceId}/user`, {
      userId
    })
      .then(() => Promise.resolve())
      .catch(errors => Promise.reject(errors));
  }
};
